import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Locale } from 'date-fns';
import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  eachDayOfInterval,
  eachMonthOfInterval,
  endOfMonth,
  endOfDay,
  endOfWeek,
  endOfYear,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  setHours,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear
} from 'date-fns';
import { toast } from 'react-hot-toast';
import { PlusIcon, ChevronLeftIcon, ChevronRightIcon, ArrowDownTrayIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { CalendarRsvpChoice, normalizeCalendarRsvpValue } from '@/components/calendar/CalendarRsvpChoice';
import {
  calendarApi,
  CalendarEventDto,
  CALENDAR_REMINDER_MINUTES,
  formatCalendarRsvpLabel
} from '@/services/calendarApi';
import { userPositionService } from '@/services/userPositionService';
import { useAuth } from '@/state/auth/AuthProvider';
import { useLanguage } from '@/state/language/LanguageProvider';
import { pePortfolioService } from '@/services/pePortfolioService';
import { getCalendarViewChildTenantId, setCalendarViewChildTenantId } from '@/services/peDelegatedContext';
import { cn } from '@/utils/cn';
import { getDateFnsLocale } from '@/utils/dateFnsLocale';
import { useUserTimeZone } from '@/hooks/useUserTimeZone';

type ViewMode = 'month' | 'week' | 'day' | 'agenda' | 'year';

function apiErr(e: unknown, fallback: string): string {
  const err = e as { response?: { data?: { error?: { message?: string }; message?: string } } };
  const m = err?.response?.data?.error?.message ?? err?.response?.data?.message;
  return typeof m === 'string' && m.length > 0 ? m : fallback;
}

/** True when the event was created by this user (organizer). */
function isCalendarOrganizer(ev: CalendarEventDto | null | undefined, userId: string): boolean {
  if (!userId || !ev?.createdByUserId) return false;
  return String(ev.createdByUserId) === String(userId);
}

const SENSITIVITY_OPTIONS = ['Public', 'Internal', 'Confidential', 'Secret', 'Top Secret'] as const;

type RecurrenceUnit = 'none' | 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';

function recurrenceConfigFromRule(
  rule: string | null | undefined
): { frequency: number; unit: RecurrenceUnit } {
  const r = (rule ?? '').toUpperCase().trim();
  if (!r) return { frequency: 1, unit: 'none' };

  const freqMatch = r.match(/FREQ=([A-Z]+)/);
  const intervalMatch = r.match(/INTERVAL=(\d+)/);
  const freq = freqMatch?.[1] ?? '';
  const interval = Math.max(1, Math.floor(Number(intervalMatch?.[1] ?? 1)));

  if (freq === 'DAILY') {
    return { frequency: interval, unit: 'daily' };
  }
  if (freq === 'WEEKLY') {
    return { frequency: interval, unit: 'weekly' };
  }
  if (freq === 'MONTHLY') {
    if (interval % 3 === 0) {
      return { frequency: Math.max(1, interval / 3), unit: 'quarterly' };
    }
    return { frequency: interval, unit: 'monthly' };
  }
  if (freq === 'YEARLY') {
    return { frequency: interval, unit: 'yearly' };
  }
  return { frequency: 1, unit: 'none' };
}

/** Recurrence category for styling (quarterly is encoded via monthly INTERVAL%3 in `recurrenceConfigFromRule`). */
type RecurrenceBand = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly' | 'none';

function recurrenceBand(ev: CalendarEventDto): RecurrenceBand {
  const cfg = recurrenceConfigFromRule(ev.recurrenceRule);
  if (!ev.recurrenceRule?.trim() || cfg.unit === 'none') return 'none';
  return cfg.unit;
}

/** Higher = more frequent (daily > weekly > …). Used to pick which meeting sets the year cell color. */
function recurrenceFrequencyScore(ev: CalendarEventDto): number {
  const cfg = recurrenceConfigFromRule(ev.recurrenceRule);
  if (!ev.recurrenceRule?.trim() || cfg.unit === 'none') return 0;
  const intv = Math.max(1, cfg.frequency);
  switch (cfg.unit) {
    case 'daily':
      return 10_000 / intv;
    case 'weekly':
      return 1_000 / intv;
    case 'monthly':
      return 100 / intv;
    case 'quarterly':
      return 10 / intv;
    case 'yearly':
      return 1 / intv;
    default:
      return 0;
  }
}

function formatYearDayHoverTooltip(
  day: Date,
  overlapping: CalendarEventDto[],
  t: (key: string) => string,
  locale: Locale
): string {
  if (overlapping.length === 0) return format(day, 'PPP', { locale });
  const sorted = [...overlapping].sort(
    (a, b) => parseISO(a.startAt).getTime() - parseISO(b.startAt).getTime()
  );
  const lines = sorted.map((ev) => {
    const s = parseISO(ev.startAt);
    const e = parseISO(ev.endAt);
    const band = recurrenceBand(ev);
    const rec = t(recurrenceLabelKey(band));
    return `${ev.title} — ${format(s, 'p', { locale })}–${format(e, 'p', { locale })} · ${rec}`;
  });
  return [format(day, 'PPP', { locale }), ...lines].join('\n');
}

/**
 * Distinct hue per frequency. Daily is text-only (no fill). Weekly is a light fill;
 * monthly / quarterly / yearly get progressively darker fills. One-off shares one color.
 */
function recurrenceVisualClass(band: RecurrenceBand): string {
  switch (band) {
    case 'daily':
      return 'bg-transparent text-amber-700 dark:text-amber-400';
    case 'weekly':
      return 'bg-emerald-400 text-emerald-950 dark:bg-emerald-500 dark:text-white';
    case 'monthly':
      return 'bg-violet-700 text-white dark:bg-violet-600';
    case 'quarterly':
      return 'box-border border-[3px] border-rose-300 bg-rose-800 text-white dark:border-rose-200 dark:bg-rose-700';
    case 'yearly':
      return 'box-border border-[3px] border-teal-300 bg-teal-950 text-white dark:border-teal-200 dark:bg-teal-900';
    case 'none':
    default:
      return 'bg-blue-600 text-white dark:bg-blue-500';
  }
}

function recurrenceHasFill(band: RecurrenceBand): boolean {
  return band !== 'daily';
}

/** Year-view day cell from winning recurrence band (highest frequency meeting). */
function yearViewDayBackgroundClass(band: RecurrenceBand): string {
  return `${recurrenceVisualClass(band)} font-semibold`;
}

const RECURRENCE_COLOR_LEGEND: { band: RecurrenceBand; labelKey: string }[] = [
  { band: 'none', labelKey: 'calendar.recurrenceLabel.oneTime' },
  { band: 'daily', labelKey: 'calendar.recurrenceLabel.daily' },
  { band: 'weekly', labelKey: 'calendar.recurrenceLabel.weekly' },
  { band: 'monthly', labelKey: 'calendar.recurrenceLabel.monthly' },
  { band: 'quarterly', labelKey: 'calendar.recurrenceLabel.quarterly' },
  { band: 'yearly', labelKey: 'calendar.recurrenceLabel.yearly' }
];

function recurrenceLabelKey(band: RecurrenceBand): string {
  switch (band) {
    case 'daily':
      return 'calendar.recurrenceLabel.daily';
    case 'weekly':
      return 'calendar.recurrenceLabel.weekly';
    case 'monthly':
      return 'calendar.recurrenceLabel.monthly';
    case 'quarterly':
      return 'calendar.recurrenceLabel.quarterly';
    case 'yearly':
      return 'calendar.recurrenceLabel.yearly';
    default:
      return 'calendar.recurrenceLabel.oneTime';
  }
}

function translateSensitivityLabel(s: string, t: (key: string) => string): string {
  const v = (s || 'Internal').trim();
  if (v === 'Top Secret') return t('calendar.sensitivity.topSecret');
  return t(`calendar.sensitivity.${v}`);
}

/** City-style label from an IANA zone, e.g. America/New_York → New York. */
function timeZoneCityLabel(timeZone: string): string {
  const city = (timeZone || '').split('/').pop() || timeZone || '';
  return city.replace(/_/g, ' ');
}

/** Live clock in the user's timezone (e.g. 8:11 AM). */
function formatLocalClock(now: Date, timeZone: string, localeId: string): string {
  try {
    return new Intl.DateTimeFormat(localeId || undefined, {
      timeZone,
      hour: 'numeric',
      minute: '2-digit'
    }).format(now);
  } catch {
    return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(now);
  }
}

function weekdayTokenFromDate(dt: Date): 'SU' | 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' {
  const wk = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as const;
  return wk[dt.getDay()];
}

/** 1..5 => first/second/.../fifth weekday-of-month for the selected date. */
function weekOrdinalInMonth(dt: Date): number {
  return Math.floor((dt.getDate() - 1) / 7) + 1;
}

function rruleFromFrequencyUnit(
  frequency: number,
  unit: RecurrenceUnit,
  start: Date
): string | null {
  const byDay = weekdayTokenFromDate(start);
  const bySetPos = weekOrdinalInMonth(start);
  const interval = Math.max(1, Math.floor(frequency || 1));
  if (unit === 'none') return null;
  if (unit === 'daily') return interval === 1 ? 'FREQ=DAILY' : `FREQ=DAILY;INTERVAL=${interval}`;
  if (unit === 'weekly')
    return interval === 1
      ? `FREQ=WEEKLY;BYDAY=${byDay}`
      : `FREQ=WEEKLY;INTERVAL=${interval};BYDAY=${byDay}`;
  if (unit === 'monthly')
    return interval === 1
      ? `FREQ=MONTHLY;BYDAY=${byDay};BYSETPOS=${bySetPos}`
      : `FREQ=MONTHLY;INTERVAL=${interval};BYDAY=${byDay};BYSETPOS=${bySetPos}`;
  if (unit === 'yearly') {
    return interval === 1 ? 'FREQ=YEARLY' : `FREQ=YEARLY;INTERVAL=${interval}`;
  }
  const quarterInterval = interval * 3;
  return `FREQ=MONTHLY;INTERVAL=${quarterInterval};BYDAY=${byDay};BYSETPOS=${bySetPos}`;
}

function toLocalDateTimeInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function nextTopOfHour(d: Date): Date {
  const n = new Date(d);
  const hasFraction = n.getMinutes() !== 0 || n.getSeconds() !== 0 || n.getMilliseconds() !== 0;
  n.setMinutes(0, 0, 0);
  if (hasFraction) n.setHours(n.getHours() + 1);
  return n;
}

function plusOneHourLocalInputValue(value: string): string | null {
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return null;
  return toLocalDateTimeInputValue(new Date(dt.getTime() + 60 * 60 * 1000));
}

/** `yyyy-MM-dd` for Dec 31 of the current calendar year (local). */
function defaultRecurrenceUntilEndOfYear(): string {
  const y = new Date().getFullYear();
  return `${y}-12-31`;
}

type TenantUserEntry = { id: string; displayName: string; email: string };

function userMatchesInviteeSearch(u: TenantUserEntry, query: string): boolean {
  const t = query.trim().toLowerCase();
  if (!t) return false;
  return u.displayName.toLowerCase().includes(t) || u.email.toLowerCase().includes(t);
}

function shiftAnchor(anchor: Date, mode: ViewMode, dir: -1 | 1): Date {
  if (mode === 'month' || mode === 'agenda') return addMonths(anchor, dir);
  if (mode === 'week') return addWeeks(anchor, dir);
  if (mode === 'year') return addYears(anchor, dir);
  return addDays(anchor, dir);
}

function startEndForView(anchor: Date, mode: ViewMode, locale: Locale): { from: Date; to: Date } {
  if (mode === 'month') {
    return { from: startOfMonth(anchor), to: endOfMonth(anchor) };
  }
  if (mode === 'week') {
    const ws = startOfWeek(anchor, { locale });
    const we = endOfWeek(anchor, { locale });
    return { from: ws, to: we };
  }
  if (mode === 'day') {
    return { from: startOfDay(anchor), to: endOfDay(anchor) };
  }
  if (mode === 'year') {
    return { from: startOfYear(anchor), to: endOfYear(anchor) };
  }
  const from = startOfMonth(anchor);
  const to = endOfMonth(anchor);
  return { from, to };
}

/** Same overlap rule as the calendar API: event intersects [from, to]. */
function eventOverlapsRange(ev: CalendarEventDto, rangeFrom: Date, rangeTo: Date): boolean {
  const s = parseISO(ev.startAt).getTime();
  const e = parseISO(ev.endAt).getTime();
  return s < rangeTo.getTime() && e > rangeFrom.getTime();
}

/** Weeks covering one calendar month (annual mini calendars); week start follows `locale`. */
function miniMonthGridDays(monthRef: Date, locale: Locale): Date[] {
  const start = startOfWeek(startOfMonth(monthRef), { locale });
  const end = endOfWeek(endOfMonth(monthRef), { locale });
  return eachDayOfInterval({ start, end });
}

/** Visible hour rows in week/day grids (local time). */
const CALENDAR_VIEW_START_HOUR = 6;
const CALENDAR_VIEW_END_HOUR = 22;

function calendarHoursInView(): number[] {
  return Array.from(
    { length: CALENDAR_VIEW_END_HOUR - CALENDAR_VIEW_START_HOUR },
    (_, i) => i + CALENDAR_VIEW_START_HOUR
  );
}

/** Vertical placement inside the day column (percentage of 6:00–22:00 band). */
function getEventBlockStyle(ev: CalendarEventDto, day: Date): { top: string; height: string } | null {
  const day0 = startOfDay(day);
  const rangeStart = setHours(day0, CALENDAR_VIEW_START_HOUR);
  const rangeEnd = setHours(day0, CALENDAR_VIEW_END_HOUR);
  const dayEndMs = endOfDay(day).getTime();
  const evS = parseISO(ev.startAt);
  const evE = parseISO(ev.endAt);
  const clipStart = Math.max(evS.getTime(), rangeStart.getTime(), day0.getTime());
  const clipEnd = Math.min(evE.getTime(), rangeEnd.getTime(), dayEndMs);
  if (clipEnd <= clipStart) return null;
  const total = rangeEnd.getTime() - rangeStart.getTime();
  if (total <= 0) return null;
  const topPct = ((clipStart - rangeStart.getTime()) / total) * 100;
  const hPct = Math.max(((clipEnd - clipStart) / total) * 100, 1.25);
  return { top: `${topPct}%`, height: `${hPct}%` };
}

/** Month / week / day chip text: time + title on start day; continuation and end-day hints for multi-day. */
function eventLabelForDayCell(
  ev: CalendarEventDto,
  day: Date,
  t: (key: string, params?: Record<string, string | number>) => string
): string {
  const s = parseISO(ev.startAt);
  const e = parseISO(ev.endAt);
  const title = ev.title;
  if (isSameDay(s, e)) {
    return `${format(s, 'HH:mm')} ${title}`;
  }
  if (isSameDay(s, day)) {
    return `${format(s, 'HH:mm')} ${title}`;
  }
  if (isSameDay(e, day)) {
    return t('calendar.event.ends', { time: format(e, 'HH:mm'), title });
  }
  return t('calendar.event.continuation', { title });
}

/** Outlook-style month: multi-day events as horizontal bars (segment within one locale week row). */
type OutlookWeekBannerSegment = {
  ev: CalendarEventDto;
  startCol: number;
  span: number;
};

function clipMultiDayEventToWeek(
  ev: CalendarEventDto,
  weekDays: Date[]
): OutlookWeekBannerSegment | null {
  if (weekDays.length !== 7) return null;
  const s = parseISO(ev.startAt);
  const e = parseISO(ev.endAt);
  if (isSameDay(s, e)) return null;

  const ws = startOfDay(weekDays[0]).getTime();
  const we = endOfDay(weekDays[6]).getTime();
  const sMs = s.getTime();
  const eMs = e.getTime();
  if (eMs <= ws || sMs >= we) return null;

  let startCol = -1;
  let endCol = -1;
  for (let i = 0; i < 7; i++) {
    const d0 = startOfDay(weekDays[i]).getTime();
    const d1 = endOfDay(weekDays[i]).getTime();
    if (eMs > d0 && sMs < d1) {
      if (startCol < 0) startCol = i;
      endCol = i;
    }
  }
  if (startCol < 0) return null;
  return { ev, startCol, span: endCol - startCol + 1 };
}

function outlookBannerSegmentsOverlap(a: OutlookWeekBannerSegment, b: OutlookWeekBannerSegment): boolean {
  const aEnd = a.startCol + a.span - 1;
  const bEnd = b.startCol + b.span - 1;
  return !(aEnd < b.startCol || bEnd < a.startCol);
}

/** Greedy lane stacking so overlapping bars sit on separate rows (Outlook-style). */
function buildOutlookWeekBannerLanes(weekDays: Date[], events: CalendarEventDto[]): OutlookWeekBannerSegment[][] {
  const segments: OutlookWeekBannerSegment[] = [];
  for (const ev of events) {
    const seg = clipMultiDayEventToWeek(ev, weekDays);
    if (seg) segments.push(seg);
  }
  if (segments.length === 0) return [];

  segments.sort((a, b) => {
    const d = a.startCol - b.startCol;
    if (d !== 0) return d;
    return b.span - a.span;
  });

  const lanes: OutlookWeekBannerSegment[][] = [];
  for (const seg of segments) {
    let placed = false;
    for (const lane of lanes) {
      if (!lane.some((s) => outlookBannerSegmentsOverlap(s, seg))) {
        lane.push(seg);
        placed = true;
        break;
      }
    }
    if (!placed) lanes.push([seg]);
  }
  return lanes;
}

function outlookBannerTooltip(ev: CalendarEventDto, locale: Locale): string {
  const s = parseISO(ev.startAt);
  const e = parseISO(ev.endAt);
  return `${ev.title} · ${format(s, 'PPp', { locale })} – ${format(e, 'p', { locale })}`;
}

type CalendarEventStyleVariant = 'banner' | 'dayCell' | 'timeBlock' | 'agenda';

/** Recurrence color on month/week/day chips. Agenda is a plain list row (no fill). */
function eventVisualClass(ev: CalendarEventDto, variant: CalendarEventStyleVariant = 'dayCell'): string {
  if (variant === 'agenda') {
    return 'rounded-lg border border-[var(--color-border)] bg-transparent text-[var(--color-primary)]';
  }
  const band = recurrenceBand(ev);
  const rounded = variant === 'banner' ? 'rounded-sm' : 'rounded-md';
  const fill = recurrenceHasFill(band) ? 'shadow-sm hover:brightness-110' : 'hover:underline';
  return `${rounded} ${recurrenceVisualClass(band)} font-semibold ${fill}`;
}

/**
 * Month banner lane: one grid child per column so `divide-x` matches the 7 day cells below.
 * A single spanning item would be the only sibling and breaks column dividers / alignment.
 */
function renderOutlookBannerLaneCells(
  lane: OutlookWeekBannerSegment[],
  laneKey: string,
  onOpenEdit: (ev: CalendarEventDto) => void,
  locale: Locale
): React.ReactNode[] {
  const cells: React.ReactNode[] = [];
  let col = 0;
  while (col < 7) {
    const seg = lane.find((s) => s.startCol === col);
    if (seg) {
      cells.push(
        <button
          key={`${laneKey}-seg-${seg.ev.id}-${seg.startCol}`}
          type="button"
          title={outlookBannerTooltip(seg.ev, locale)}
          style={{ gridColumn: `span ${seg.span}` }}
          onClick={() => void onOpenEdit(seg.ev)}
          className={`min-h-[1.5rem] truncate px-1.5 py-0.5 text-left text-xs font-medium leading-tight sm:min-h-7 sm:text-sm ${eventVisualClass(seg.ev, 'banner')}`}
        >
          {seg.ev.title}
        </button>
      );
      col += seg.span;
    } else {
      cells.push(
        <div key={`${laneKey}-ph-${col}`} aria-hidden className="min-h-[1.35rem] bg-transparent sm:min-h-6" />
      );
      col += 1;
    }
  }
  return cells;
}

/** Compact sensitivity pill (agenda meta row). White-on-solid so it stays readable on the filled row. */
function eventBadgeClass(ev: CalendarEventDto): string {
  const base = 'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase text-white/95 ring-1 ring-white/35';
  if (ev.visibilityReason === 'invited') {
    return `${base} bg-violet-800/70`;
  }
  switch ((ev.sensitivity || 'Internal').trim()) {
    case 'Public':
      return `${base} bg-emerald-800/70`;
    case 'Internal':
      return `${base} bg-blue-900/60`;
    case 'Confidential':
      return `${base} bg-amber-800/70`;
    case 'Secret':
      return `${base} bg-orange-800/70`;
    case 'Top Secret':
      return `${base} bg-red-900/70`;
    default:
      return `${base} bg-indigo-800/70`;
  }
}

export const CalendarPage: React.FC = () => {
  const { user } = useAuth();
  const { t, currentLanguage } = useLanguage();
  const dfLocale = useMemo(() => getDateFnsLocale(currentLanguage.id), [currentLanguage.id]);
  const { timeZone: tz } = useUserTimeZone();
  const [clockNow, setClockNow] = useState(() => new Date());
  const currentUserId = user?.id ?? '';
  const [searchParams, setSearchParams] = useSearchParams();
  const isPeFirm = user?.tenantType === 'PORTFOLIO';

  const [anchor, setAnchor] = useState(() => new Date());
  const [view, setView] = useState<ViewMode>('month');
  const [events, setEvents] = useState<CalendarEventDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [formTitle, setFormTitle] = useState('');
  const [formStart, setFormStart] = useState('');
  const [formEnd, setFormEnd] = useState('');
  const [formSensitivity, setFormSensitivity] = useState<string>('Internal');
  const [formDescription, setFormDescription] = useState('');
  const [formPhysicalLocation, setFormPhysicalLocation] = useState('');
  const [formMeetingUrl, setFormMeetingUrl] = useState('');
  const [formInviteeIds, setFormInviteeIds] = useState<string[]>([]);
  const [isEndManuallySet, setIsEndManuallySet] = useState(false);
  const [inviteeSearchQuery, setInviteeSearchQuery] = useState('');
  const [inviteeActiveIndex, setInviteeActiveIndex] = useState(0);
  const [modalDetail, setModalDetail] = useState<CalendarEventDto | null>(null);
  const [tenantUserDirectory, setTenantUserDirectory] = useState<TenantUserEntry[]>([]);
  const [formReminders, setFormReminders] = useState<number[]>([]);
  const [formRecurrenceFrequency, setFormRecurrenceFrequency] = useState<number>(1);
  const [formRecurrenceUnit, setFormRecurrenceUnit] = useState<RecurrenceUnit>('none');
  const [formRecurrenceEnd, setFormRecurrenceEnd] = useState('');
  const [formRecurrenceForever, setFormRecurrenceForever] = useState(true);
  const [formDateScope, setFormDateScope] = useState<'this_occurrence' | 'series'>('this_occurrence');
  const [peCompanies, setPeCompanies] = useState<Array<{ id: string; name: string }>>([]);
  const [calendarPeChildId, setCalendarPeChildId] = useState(() => getCalendarViewChildTenantId() ?? '');

  useEffect(() => {
    const id = window.setInterval(() => setClockNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const localClockLabel = useMemo(
    () => formatLocalClock(clockNow, tz, currentLanguage.id),
    [clockNow, tz, currentLanguage.id]
  );
  const localZoneCity = useMemo(() => timeZoneCityLabel(tz), [tz]);

  /** PE user viewing a portfolio company calendar (session child tenant) — read-only; no new events. */
  const isPePortfolioCompanyCalendar = isPeFirm && calendarPeChildId.trim().length > 0;

  /**
   * Load events: full month for month/week/day/agenda (shared cache).
   * Full calendar year when annual view is active.
   */
  const load = useCallback(async () => {
    const { from, to } = startEndForView(anchor, view === 'year' ? 'year' : 'month', dfLocale);
    setLoading(true);
    try {
      const { data: body } = await calendarApi.list(from.toISOString(), to.toISOString(), false);
      setEvents(body.data ?? []);
    } catch (e) {
      toast.error(apiErr(e, t('calendar.error.loadFailed')));
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [anchor, calendarPeChildId, view, t, dfLocale]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadEventByFetchId = useCallback(async (fetchId: string) => {
    try {
      const { data: body } = await calendarApi.get(fetchId);
      const d = body.data;
      setEditingId(d.id);
      setFormTitle(d.title);
      setFormDescription(d.description ?? '');
      setFormPhysicalLocation(d.location?.trim() ? d.location : '');
      setFormMeetingUrl(d.onlineMeetingUrl?.trim() ? d.onlineMeetingUrl : '');
      setFormSensitivity(d.sensitivity);
      const s = parseISO(d.startAt);
      const e = parseISO(d.endAt);
      setFormStart(toLocalDateTimeInputValue(s));
      setFormEnd(toLocalDateTimeInputValue(e));
      setIsEndManuallySet(false);
      const ids = (d.invitations ?? [])
        .map((i) => i.inviteeUserId)
        .filter((x): x is string => typeof x === 'string' && x.length > 0);
      setFormInviteeIds(ids);
      setInviteeSearchQuery('');
      setModalDetail(d);
      setFormReminders(d.reminderOffsetMinutes ?? []);
      const recurrenceCfg = recurrenceConfigFromRule(d.recurrenceRule);
      setFormRecurrenceFrequency(recurrenceCfg.frequency);
      setFormRecurrenceUnit(recurrenceCfg.unit);
      const hasUntil = Boolean(d.recurrenceEndAt);
      setFormRecurrenceForever(recurrenceCfg.unit !== 'none' && !hasUntil);
      setFormRecurrenceEnd(
        hasUntil && d.recurrenceEndAt
          ? format(parseISO(d.recurrenceEndAt), 'yyyy-MM-dd')
          : ''
      );
      setFormDateScope(d.occurrenceStartAt ? 'this_occurrence' : 'series');
      setModalOpen(true);
    } catch (err) {
      toast.error(apiErr(err, t('calendar.error.loadEventFailed')));
    }
  }, [t]);

  const [recurringDeclineOpen, setRecurringDeclineOpen] = useState(false);
  const [recurringCancelOpen, setRecurringCancelOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<CalendarEventDto | null>(null);

  const submitRsvp = useCallback(
    async (
      v: 'none' | 'accepted' | 'declined' | 'tentative',
      opts?: { rsvpScope?: 'series' | 'this_occurrence' }
    ) => {
      if (!editingId) return;
      try {
        const { data: b } = await calendarApi.setMyRsvp(editingId, v, opts);
        setModalDetail(b.data);
        await load();
        const msg =
          v === 'declined'
            ? t('calendar.toast.rsvpDeclined')
            : v === 'accepted'
              ? t('calendar.toast.rsvpAccepted')
              : t('calendar.toast.rsvpUpdated');
        toast.success(msg);
      } catch (err) {
        toast.error(apiErr(err, t('calendar.error.updateRsvpFailed')));
      }
    },
    [editingId, load, t]
  );

  const openEventQueryId = searchParams.get('openEvent');

  /** Open event from dashboard (or other links): /calendar?openEvent=<id> */
  useEffect(() => {
    if (!openEventQueryId) return;
    let cancelled = false;
    void loadEventByFetchId(openEventQueryId).finally(() => {
      if (cancelled) return;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('openEvent');
          return next;
        },
        { replace: true }
      );
    });
    return () => {
      cancelled = true;
    };
  }, [openEventQueryId, loadEventByFetchId, setSearchParams]);

  useEffect(() => {
    if (!isPeFirm) return;
    void pePortfolioService
      .listCompanies()
      .then((list) => {
        setPeCompanies(
          list
            .filter((c) => c.status === 'active')
            .map((c) => ({
              id: c.childTenantId,
              name: (c.childTenantName || c.childTenantDomain || c.childTenantId).trim()
            }))
        );
      })
      .catch(() => setPeCompanies([]));
  }, [isPeFirm]);

  useEffect(() => {
    if (!modalOpen) return;
    void (async () => {
      try {
        const list = await userPositionService.getUsersWithPositions();
        setTenantUserDirectory(
          list.map((u) => {
            const email = (u.email ?? '').trim();
            const dn = `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim();
            return {
              id: u.id,
              email,
              displayName: dn || email || u.id
            };
          })
        );
      } catch {
        setTenantUserDirectory([]);
      }
    })();
  }, [modalOpen]);

  const monthGridDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(anchor), { locale: dfLocale });
    const end = endOfWeek(endOfMonth(anchor), { locale: dfLocale });
    return eachDayOfInterval({ start, end });
  }, [anchor, dfLocale]);

  /**
   * Single-calendar-day events per cell (Outlook-style: multi-day items use week banner bars, not repeated chips).
   */
  const eventsByDay = useMemo(() => {
    const m = new Map<string, CalendarEventDto[]>();
    for (const day of monthGridDays) {
      const key = format(day, 'yyyy-MM-dd');
      const list = events
        .filter((ev) => {
          if (!eventOverlapsRange(ev, startOfDay(day), endOfDay(day))) return false;
          return isSameDay(parseISO(ev.startAt), parseISO(ev.endAt));
        })
        .sort((a, b) => parseISO(a.startAt).getTime() - parseISO(b.startAt).getTime());
      m.set(key, list);
    }
    return m;
  }, [events, monthGridDays]);

  const monthWeeks = useMemo(() => {
    const days = monthGridDays;
    const weeks: Date[][] = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }
    return weeks;
  }, [monthGridDays]);

  const outlookWeekBannerLanes = useMemo(
    () => monthWeeks.map((wd) => buildOutlookWeekBannerLanes(wd, events)),
    [monthWeeks, events]
  );

  const visibleEvents = useMemo(() => {
    if (view === 'month' || view === 'agenda') {
      return events;
    }
    if (view === 'year') {
      const from = startOfYear(anchor);
      const to = endOfYear(anchor);
      return events.filter((ev) => eventOverlapsRange(ev, from, to));
    }
    if (view === 'week') {
      const from = startOfWeek(anchor, { locale: dfLocale });
      const to = endOfWeek(anchor, { locale: dfLocale });
      return events.filter((ev) => eventOverlapsRange(ev, from, to));
    }
    if (view === 'day') {
      const from = startOfDay(anchor);
      const to = endOfDay(anchor);
      return events.filter((ev) => eventOverlapsRange(ev, from, to));
    }
    return events;
  }, [events, view, anchor, dfLocale]);

  const yearMonths = useMemo(
    () => eachMonthOfInterval({ start: startOfYear(anchor), end: endOfYear(anchor) }),
    [anchor]
  );

  /**
   * Per-day: color from the overlapping meeting with the highest recurrence frequency;
   * tooltip lists all meetings that day.
   */
  const yearDayCellInfo = useMemo(() => {
    const map = new Map<string, { band: RecurrenceBand; tooltip: string }>();
    const y0 = startOfYear(anchor);
    const y1 = endOfYear(anchor);
    for (let d = y0; d <= y1; d = addDays(d, 1)) {
      const dayStart = startOfDay(d);
      const dayEnd = endOfDay(d);
      const overlapping = events.filter((ev) => eventOverlapsRange(ev, dayStart, dayEnd));
      if (overlapping.length === 0) continue;
      let best = overlapping[0]!;
      let bestScore = recurrenceFrequencyScore(best);
      for (let i = 1; i < overlapping.length; i++) {
        const ev = overlapping[i]!;
        const sc = recurrenceFrequencyScore(ev);
        if (sc > bestScore) {
          bestScore = sc;
          best = ev;
        } else if (sc === bestScore && parseISO(ev.startAt).getTime() < parseISO(best.startAt).getTime()) {
          best = ev;
        }
      }
      const band = recurrenceBand(best);
      map.set(format(d, 'yyyy-MM-dd'), {
        band,
        tooltip: formatYearDayHoverTooltip(d, overlapping, t, dfLocale)
      });
    }
    return map;
  }, [events, anchor, t, dfLocale]);

  const agendaEventsSorted = useMemo(
    () =>
      [...visibleEvents].sort(
        (a, b) => parseISO(a.startAt).getTime() - parseISO(b.startAt).getTime()
      ),
    [visibleEvents]
  );

  const weekDaysForGrid = useMemo(
    () =>
      eachDayOfInterval({
        start: startOfWeek(anchor, { locale: dfLocale }),
        end: endOfWeek(anchor, { locale: dfLocale })
      }),
    [anchor, dfLocale]
  );

  const inviteeSuggestions = useMemo(
    () =>
      tenantUserDirectory
        .filter((u) => u.id !== currentUserId && !formInviteeIds.includes(u.id))
        .filter((u) => userMatchesInviteeSearch(u, inviteeSearchQuery))
        .slice(0, 8),
    [tenantUserDirectory, formInviteeIds, currentUserId, inviteeSearchQuery]
  );

  useEffect(() => {
    setInviteeActiveIndex(0);
  }, [inviteeSearchQuery, inviteeSuggestions.length]);

  const inviteeLabelForId = useCallback(
    (id: string) => {
      const fromDir = tenantUserDirectory.find((u) => u.id === id);
      if (fromDir) return fromDir.displayName;
      const inv = modalDetail?.invitations?.find((i) => i.inviteeUserId === id);
      if (inv) return inv.displayName ?? inv.inviteeEmail ?? id;
      return id;
    },
    [tenantUserDirectory, modalDetail]
  );

  const modalIsOrganizer = !editingId || isCalendarOrganizer(modalDetail, currentUserId);
  const readOnlyMeeting = Boolean(editingId && !modalIsOrganizer);
  const lockSeriesRecurrence =
    Boolean(modalDetail?.occurrenceStartAt) && formDateScope === 'this_occurrence';

  const viewerIsInvitee = useMemo(
    () =>
      Boolean(
        editingId &&
          modalDetail &&
          (modalDetail.isInvited === true ||
            (modalDetail.invitations?.some((i) => i.inviteeUserId === currentUserId) ?? false))
      ),
    [editingId, modalDetail, currentUserId]
  );

  /** Invited attendee (not the organizer): can accept/decline without editing the event. */
  const showInviteeRsvp = Boolean(viewerIsInvitee && editingId && !modalIsOrganizer);

  const modalIsRecurringInstance = Boolean(
    editingId?.startsWith('occ:') && modalDetail?.seriesEventId
  );

  const myEffectiveRsvp = useMemo(() => {
    const fromDto = modalDetail?.myRsvp;
    if (fromDto != null && fromDto !== '') return fromDto;
    return (
      modalDetail?.invitations?.find((i) => i.inviteeUserId === currentUserId)?.rsvp ?? 'none'
    );
  }, [modalDetail, currentUserId]);

  const openCreate = () => {
    if (isPePortfolioCompanyCalendar) return;
    setEditingId(null);
    const now = nextTopOfHour(new Date());
    const end = new Date(now.getTime() + 60 * 60 * 1000);
    setFormTitle('');
    setFormDescription('');
    setFormPhysicalLocation('');
    setFormMeetingUrl('');
    setFormSensitivity('Internal');
    setFormStart(toLocalDateTimeInputValue(now));
    setFormEnd(toLocalDateTimeInputValue(end));
    setIsEndManuallySet(false);
    setFormInviteeIds([]);
    setInviteeSearchQuery('');
    setModalDetail(null);
    setFormReminders([]);
    setFormRecurrenceFrequency(1);
    setFormRecurrenceUnit('none');
    setFormRecurrenceEnd('');
    setFormRecurrenceForever(true);
    setFormDateScope('series');
    setModalOpen(true);
  };

  const openEdit = async (ev: CalendarEventDto) => {
    await loadEventByFetchId(ev.id);
  };

  const downloadIcs = async () => {
    const id = modalDetail?.id ?? editingId;
    if (!id) return;
    try {
      const res = await calendarApi.downloadIcs(id);
      const blob = new Blob([res.data], { type: 'text/calendar;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'event.ics';
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('calendar.toast.icsDownloaded'));
    } catch (e) {
      toast.error(apiErr(e, t('calendar.toast.icsFailed')));
    }
  };

  const saveEvent = async () => {
    if (readOnlyMeeting) return;
    if (!formTitle.trim()) {
      toast.error(t('calendar.validation.titleRequired'));
      return;
    }
    const start = new Date(formStart);
    const end = new Date(formEnd);
    if (!(start < end)) {
      toast.error(t('calendar.validation.endAfterStart'));
      return;
    }
    if (!Number.isFinite(formRecurrenceFrequency) || formRecurrenceFrequency < 1) {
      toast.error(t('calendar.validation.frequencyMin'));
      return;
    }
    const recurrenceRule = rruleFromFrequencyUnit(formRecurrenceFrequency, formRecurrenceUnit, start);
    if (formRecurrenceUnit !== 'none' && !formRecurrenceForever && !formRecurrenceEnd.trim()) {
      toast.error(t('calendar.validation.untilRequired'));
      return;
    }
    const recurrenceEndAt =
      formRecurrenceUnit !== 'none' && !formRecurrenceForever && formRecurrenceEnd.trim()
        ? endOfDay(parseISO(formRecurrenceEnd.trim())).toISOString()
        : null;
    const isOccurrenceEdit = Boolean(modalDetail?.occurrenceStartAt);
    const editScope = isOccurrenceEdit ? formDateScope : 'series';

    try {
      if (editingId) {
        await calendarApi.update(editingId, {
          title: formTitle.trim(),
          description: formDescription || null,
          location: formPhysicalLocation.trim() || null,
          onlineMeetingUrl: formMeetingUrl.trim() || null,
          timezone: tz,
          startAt: start.toISOString(),
          endAt: end.toISOString(),
          sensitivity: formSensitivity,
          inviteeUserIds: formInviteeIds,
          reminderOffsetMinutes: formReminders,
          ...(editScope === 'series'
            ? {
                recurrenceRule: formRecurrenceUnit === 'none' ? null : recurrenceRule,
                recurrenceEndAt: formRecurrenceUnit === 'none' ? null : recurrenceEndAt
              }
            : {}),
          editScope
        });
        toast.success(t('calendar.toast.eventUpdated'));
      } else {
        await calendarApi.create({
          title: formTitle.trim(),
          description: formDescription || null,
          location: formPhysicalLocation.trim() || null,
          onlineMeetingUrl: formMeetingUrl.trim() || null,
          timezone: tz,
          startAt: start.toISOString(),
          endAt: end.toISOString(),
          allDay: false,
          sensitivity: formSensitivity,
          reminderOffsetMinutes: formReminders,
          inviteeUserIds: formInviteeIds,
          recurrenceRule: recurrenceRule ?? null,
          recurrenceEndAt: recurrenceRule ? recurrenceEndAt : null
        });
        toast.success(t('calendar.toast.eventCreated'));
      }
      setModalOpen(false);
      await load();
    } catch (e) {
      toast.error(apiErr(e, t('calendar.toast.saveFailed')));
    }
  };

  const performCancel = async (ev: CalendarEventDto, scope: 'this_occurrence' | 'series') => {
    if (!isCalendarOrganizer(ev, currentUserId)) return;
    try {
      await calendarApi.cancel(ev.id, { cancelScope: scope });
      toast.success(
        scope === 'this_occurrence'
          ? t('calendar.toast.occurrenceCancelled')
          : t('calendar.toast.eventCancelled')
      );
      setRecurringCancelOpen(false);
      setCancelTarget(null);
      setModalOpen(false);
      await load();
    } catch (e) {
      toast.error(apiErr(e, t('calendar.toast.cancelFailed')));
    }
  };

  const requestCancel = (ev: CalendarEventDto) => {
    if (!isCalendarOrganizer(ev, currentUserId)) return;
    if (ev.occurrenceStartAt || ev.seriesEventId) {
      setCancelTarget(ev);
      setRecurringCancelOpen(true);
      return;
    }
    void performCancel(ev, 'series');
  };

  return (
    <div className="min-h-screen bg-[var(--color-surface-page)] p-4 md:p-6">
      <div className="mx-auto max-w-6xl">
        <header className="relative mb-4 grid grid-cols-1 items-center gap-3 sm:grid-cols-[1fr_auto_1fr]">
          <div className="min-w-0 sm:justify-self-start">
            {isPeFirm && peCompanies.length > 0 && (
              <label className="block max-w-xs text-xs font-medium text-[var(--color-secondary)]">
                {t('calendar.pe.portfolioCompany')}
                <select
                  className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-page)] px-3 py-2 text-sm text-[var(--color-primary)]"
                  value={calendarPeChildId}
                  onChange={(e) => {
                    const v = e.target.value;
                    setCalendarPeChildId(v);
                    setCalendarViewChildTenantId(v || null);
                  }}
                >
                  <option value="">{t('calendar.pe.myFirm')}</option>
                  {peCompanies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          <div className="flex items-center justify-center gap-2">
            <button
              type="button"
              aria-label={t('calendar.aria.previous')}
              className="rounded-md p-2 text-[var(--color-primary)] hover:bg-[var(--color-surface-elevated)]"
              onClick={() => setAnchor((a) => shiftAnchor(a, view, -1))}
            >
              <ChevronLeftIcon className="h-6 w-6" />
            </button>
            <div className="min-w-[14rem] text-center">
              <h1 className="text-2xl font-bold leading-tight text-[var(--color-primary)] sm:text-3xl">
                {view === 'day'
                  ? format(anchor, 'PPP', { locale: dfLocale })
                  : view === 'week'
                    ? t('calendar.weekOf', {
                        date: format(startOfWeek(anchor, { locale: dfLocale }), 'MMM d, yyyy', {
                          locale: dfLocale
                        })
                      })
                    : view === 'year'
                      ? format(anchor, 'yyyy')
                      : format(anchor, 'MMMM yyyy', { locale: dfLocale })}
              </h1>
              <div className="mt-1 text-sm text-[var(--color-secondary)] sm:text-base">
                {t('calendar.clock.localNow', { time: localClockLabel, zone: localZoneCity })}
              </div>
            </div>
            <button
              type="button"
              aria-label={t('calendar.aria.next')}
              className="rounded-md p-2 text-[var(--color-primary)] hover:bg-[var(--color-surface-elevated)]"
              onClick={() => setAnchor((a) => shiftAnchor(a, view, 1))}
            >
              <ChevronRightIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="flex items-center justify-end gap-3">
            {loading && <span className="text-xs text-[var(--color-secondary)]">{t('common.loading')}</span>}
            {!isPePortfolioCompanyCalendar ? (
              <button
                type="button"
                onClick={openCreate}
                className="inline-flex items-center justify-center rounded-md bg-[var(--color-accent-bg)] px-4 py-2 text-sm font-medium text-[var(--color-accent-text)] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
              >
                <PlusIcon className="mr-2 h-5 w-5" aria-hidden />
                {t('calendar.newEvent')}
              </button>
            ) : null}
          </div>
        </header>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          {(['year', 'month', 'week', 'day', 'agenda'] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              className={`rounded-md px-3 py-1.5 text-sm sm:text-base ${
                view === v
                  ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]'
                  : 'bg-[var(--color-surface-elevated)] text-[var(--color-secondary)] ring-1 ring-[var(--color-border)]'
              }`}
            >
              {t(`calendar.view.${v}`)}
            </button>
          ))}
        </div>

        <ul className="mb-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-[var(--color-secondary)] sm:text-sm">
          {RECURRENCE_COLOR_LEGEND.map((item) => (
            <li key={item.band} className="inline-flex items-center gap-1.5">
              <span
                className={
                  recurrenceHasFill(item.band)
                    ? `inline-block h-2.5 w-2.5 rounded-sm ${recurrenceVisualClass(item.band)}`
                    : `inline-flex h-2.5 w-2.5 items-center justify-center rounded-sm border border-current text-[8px] font-bold leading-none ${recurrenceVisualClass(item.band)}`
                }
                aria-hidden
              >
                {recurrenceHasFill(item.band) ? null : 'A'}
              </span>
              {t(item.labelKey)}
            </li>
          ))}
        </ul>

        {view === 'agenda' && (
          <ul className="space-y-2 rounded-lg border border-[var(--color-border)] bg-transparent p-4">
            {agendaEventsSorted.length === 0 && !loading && (
              <li className="text-sm text-[var(--color-secondary)]">{t('calendar.agenda.empty')}</li>
            )}
            {agendaEventsSorted.map((ev) => (
              <li
                key={ev.id}
                className={`flex flex-col gap-1 p-3 sm:flex-row sm:items-center sm:justify-between ${eventVisualClass(ev, 'agenda')}`}
              >
                <div>
                  <div className="font-semibold text-[var(--color-primary)]">{ev.title}</div>
                  <div className="text-xs text-[var(--color-secondary)]">
                    {format(parseISO(ev.startAt), 'PPp', { locale: dfLocale })} –{' '}
                    {format(parseISO(ev.endAt), 'p', { locale: dfLocale })}
                    <span className={`ml-2 ${eventBadgeClass(ev)}`}>
                      {translateSensitivityLabel(ev.sensitivity, t)}
                      {ev.isInvited ? t('calendar.badge.invitedSuffix') : ''}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="text-sm font-medium text-[var(--color-primary)] underline-offset-2 hover:underline"
                    onClick={() => void openEdit(ev)}
                  >
                    {isCalendarOrganizer(ev, currentUserId)
                      ? t('calendar.action.edit')
                      : t('calendar.action.view')}
                  </button>
                  {isCalendarOrganizer(ev, currentUserId) && (
                    <button
                      type="button"
                      className="text-sm font-medium text-red-700 underline-offset-2 hover:underline"
                      onClick={() => requestCancel(ev)}
                    >
                      {t('calendar.action.cancelMeeting')}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {view === 'year' && (
          <div className="overflow-x-auto rounded-lg border border-[var(--color-border)] bg-transparent p-3 sm:p-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
              {yearMonths.map((monthDate) => {
                const days = miniMonthGridDays(monthDate, dfLocale);
                const weeks: Date[][] = [];
                for (let i = 0; i < days.length; i += 7) {
                  weeks.push(days.slice(i, i + 7));
                }
                return (
                  <div
                    key={format(monthDate, 'yyyy-MM')}
                    className="rounded-lg border border-[var(--color-border)] bg-transparent p-2"
                  >
                    <button
                      type="button"
                      className="mb-2 w-full text-center text-sm font-semibold text-[var(--color-primary)] hover:underline"
                      onClick={() => {
                        setAnchor(startOfMonth(monthDate));
                        setView('month');
                      }}
                    >
                      {format(monthDate, 'MMMM', { locale: dfLocale })}
                    </button>
                    <div className="grid grid-cols-7 gap-px text-center text-xs text-[var(--color-secondary)]">
                      {days.slice(0, 7).map((d0) => (
                        <div key={d0.toISOString()} className="pb-0.5 font-bold">
                          {format(d0, 'EEE', { locale: dfLocale })}
                        </div>
                      ))}
                    </div>
                    <div className="space-y-px">
                      {weeks.map((week) => (
                        <div key={format(week[0]!, 'yyyy-MM-dd')} className="grid grid-cols-7 gap-px">
                          {week.map((day) => {
                            const dayKey = format(day, 'yyyy-MM-dd');
                            const muted = !isSameMonth(day, monthDate);
                            const cell = yearDayCellInfo.get(dayKey);
                            const has = cell != null;
                            return (
                              <button
                                key={dayKey}
                                type="button"
                                title={cell?.tooltip ?? format(day, 'PPP', { locale: dfLocale })}
                                onClick={() => {
                                  setAnchor(day);
                                  setView('month');
                                }}
                                className={cn(
                                  'flex min-h-[1.5rem] flex-col items-center justify-center rounded p-0.5 text-xs tabular-nums focus:outline-none focus:ring-1 focus:ring-[var(--color-info)]',
                                  has
                                    ? cn(
                                        yearViewDayBackgroundClass(cell.band),
                                        'hover:brightness-[1.07] dark:hover:brightness-110',
                                        muted && 'opacity-65'
                                      )
                                    : cn(
                                        muted
                                          ? 'text-[var(--color-secondary)] opacity-45'
                                          : 'text-[var(--color-primary)]',
                                        'hover:bg-[var(--color-accent-bg)]/15'
                                      )
                                )}
                              >
                                <span>{format(day, 'd')}</span>
                              </button>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            {!loading && visibleEvents.length === 0 && (
              <p className="mt-3 text-center text-xs text-[var(--color-secondary)]">{t('calendar.year.empty')}</p>
            )}
          </div>
        )}

        {view === 'month' && (
          <div className="overflow-x-auto rounded-lg border border-[var(--color-border)] bg-transparent">
            <div>
              <div className="grid grid-cols-7 divide-x divide-[var(--color-border)] border-b border-[var(--color-border)] bg-transparent">
                {monthGridDays.slice(0, 7).map((d0) => (
                  <div
                    key={d0.toISOString()}
                    className="bg-transparent p-2 text-center text-sm font-bold text-[var(--color-secondary)]"
                  >
                    {format(d0, 'EEE', { locale: dfLocale })}
                  </div>
                ))}
              </div>

              {monthWeeks.map((weekDays, wi) => {
                const lanes = outlookWeekBannerLanes[wi] ?? [];
                return (
                  <div
                    key={`w-${format(weekDays[0], 'yyyy-MM-dd')}`}
                    className="border-t border-[var(--color-border)] first:border-t-0"
                  >
                    {lanes.length > 0 && (
                      <div className="divide-y divide-[var(--color-border)] border-b border-[var(--color-border)]">
                        {lanes.map((lane, li) => (
                          <div
                            key={`lane-${wi}-${li}`}
                            className="grid grid-cols-7 divide-x divide-[var(--color-border)] bg-transparent"
                          >
                            {renderOutlookBannerLaneCells(lane, `${wi}-${li}`, openEdit, dfLocale)}
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="grid grid-cols-7 divide-x divide-[var(--color-border)] bg-transparent">
                      {weekDays.map((day) => {
                        const key = format(day, 'yyyy-MM-dd');
                        const dayEvents = eventsByDay.get(key) ?? [];
                        const muted = !isSameMonth(day, anchor);
                        return (
                          <div
                            key={key}
                            className={`flex min-h-[5.5rem] flex-col bg-transparent p-1 sm:min-h-[6rem] ${muted ? 'opacity-50' : ''}`}
                          >
                            <div className="shrink-0 text-left text-sm font-medium tabular-nums text-[var(--color-secondary)]">
                              {format(day, 'd')}
                            </div>
                            <div className="mt-1 flex min-h-0 flex-1 flex-col space-y-0.5">
                              {dayEvents.slice(0, 3).map((ev) => (
                                <button
                                  key={ev.id}
                                  type="button"
                                  title={ev.title}
                                  onClick={() => void openEdit(ev)}
                                  className={`block w-full shrink-0 truncate px-1 py-0.5 text-left text-xs ${eventVisualClass(ev, 'dayCell')}`}
                                >
                                  {eventLabelForDayCell(ev, day, t)}
                                </button>
                              ))}
                              {dayEvents.length > 3 && (
                                <div className="text-xs text-[var(--color-secondary)]">
                                  {t('calendar.month.more', { count: dayEvents.length - 3 })}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {view === 'day' && (
          <div className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-transparent">
            <p className="border-b border-[var(--color-border)] bg-transparent px-3 py-2 text-xs text-[var(--color-secondary)]">
              {t('calendar.day.intro', {
                startHour: String(CALENDAR_VIEW_START_HOUR),
                endHour: String(CALENDAR_VIEW_END_HOUR)
              })}
            </p>
            <div className="flex min-h-[12rem] bg-transparent">
              <div
                className="w-12 shrink-0 border-r border-[var(--color-border)] sm:w-14"
                aria-hidden
              >
                {calendarHoursInView().map((h) => (
                  <div
                    key={h}
                    className="flex h-12 items-start justify-end border-b border-[var(--color-border)]/60 pr-1 pt-0.5 text-xs tabular-nums text-[var(--color-secondary)] sm:text-sm"
                  >
                    {format(setHours(startOfDay(anchor), h), 'h a', { locale: dfLocale })}
                  </div>
                ))}
              </div>
              <div className="relative min-h-0 min-w-0 flex-1">
                {calendarHoursInView().map((h) => (
                  <div key={h} className="h-12 border-b border-[var(--color-border)]/40 bg-transparent" />
                ))}
                <div className="pointer-events-none absolute inset-0">
                  {visibleEvents.map((ev) => {
                    const st = getEventBlockStyle(ev, anchor);
                    if (!st) return null;
                    return (
                      <button
                        key={ev.id}
                        type="button"
                        style={{ top: st.top, height: st.height }}
                        title={ev.title}
                        className={`pointer-events-auto absolute left-1 right-1 overflow-hidden px-1 py-px text-left text-xs leading-tight sm:text-sm ${eventVisualClass(ev, 'timeBlock')}`}
                        onClick={() => void openEdit(ev)}
                      >
                        <span className="line-clamp-2 font-medium">{eventLabelForDayCell(ev, anchor, t)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            {!loading && visibleEvents.length === 0 && events.length > 0 && (
              <p className="border-t border-[var(--color-border)] px-3 py-2 text-xs text-[var(--color-secondary)]">
                {t('calendar.day.noEvents')}
              </p>
            )}
          </div>
        )}

        {view === 'week' && (
          <div className="overflow-x-auto rounded-lg border border-[var(--color-border)] bg-transparent">
            <p className="border-b border-[var(--color-border)] bg-transparent px-3 py-2 text-xs text-[var(--color-secondary)]">
              {t('calendar.week.intro', {
                startHour: String(CALENDAR_VIEW_START_HOUR),
                endHour: String(CALENDAR_VIEW_END_HOUR)
              })}
            </p>
            <div className="inline-flex min-w-full min-h-[12rem] bg-transparent">
              <div className="w-12 shrink-0 border-r border-[var(--color-border)] sm:w-14" aria-hidden>
                <div className="h-11 border-b border-[var(--color-border)] sm:h-12" />
                {calendarHoursInView().map((h) => (
                  <div
                    key={h}
                    className="flex h-12 items-start justify-end border-b border-[var(--color-border)]/60 pr-1 pt-0.5 text-xs tabular-nums text-[var(--color-secondary)] sm:text-sm"
                  >
                    {format(setHours(startOfWeek(anchor, { locale: dfLocale }), h), 'h a', {
                      locale: dfLocale
                    })}
                  </div>
                ))}
              </div>
              <div className="grid min-w-0 flex-1 grid-cols-7 divide-x divide-[var(--color-border)]">
                {weekDaysForGrid.map((day) => (
                  <div key={day.toISOString()} className="min-w-[4.5rem] sm:min-w-[6rem]">
                    <div className="flex h-11 flex-col items-center justify-center border-b border-[var(--color-border)] bg-transparent px-0.5 text-center sm:h-12">
                      <span className="text-xs font-bold uppercase text-[var(--color-secondary)] sm:text-sm">
                        {format(day, 'EEE', { locale: dfLocale })}
                      </span>
                      <span className="text-sm font-semibold text-[var(--color-primary)]">{format(day, 'd')}</span>
                    </div>
                    <div className="relative">
                      {calendarHoursInView().map((h) => (
                        <div
                          key={h}
                          className="h-12 border-b border-[var(--color-border)]/40 bg-transparent"
                        />
                      ))}
                      <div className="pointer-events-none absolute inset-0">
                        {visibleEvents.map((ev) => {
                          const st = getEventBlockStyle(ev, day);
                          if (!st) return null;
                          return (
                            <button
                              key={`${ev.id}-${format(day, 'yyyy-MM-dd')}`}
                              type="button"
                              style={{ top: st.top, height: st.height }}
                              title={ev.title}
                              className={`pointer-events-auto absolute left-0.5 right-0.5 overflow-hidden px-0.5 py-px text-left text-xs leading-tight sm:text-sm ${eventVisualClass(ev, 'timeBlock')}`}
                              onClick={() => void openEdit(ev)}
                            >
                              <span className="line-clamp-2 font-medium">
                                {eventLabelForDayCell(ev, day, t)}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {!loading && visibleEvents.length === 0 && events.length > 0 && (
              <p className="border-t border-[var(--color-border)] px-3 py-2 text-xs text-[var(--color-secondary)]">
                {t('calendar.week.noEvents')}
              </p>
            )}
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/80 p-3 sm:p-4">
          <div
            className="flex max-h-[min(88vh,40rem)] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)] shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cal-dialog-title"
          >
            <div className="shrink-0 border-b border-[var(--color-border)] bg-[var(--color-surface-bg)] px-4 py-2.5 sm:px-5 sm:py-3">
              <h2 id="cal-dialog-title" className="text-base font-semibold text-[var(--color-primary)] sm:text-lg">
                {editingId
                  ? readOnlyMeeting
                    ? t('calendar.modal.title.details')
                    : t('calendar.modal.title.edit')
                  : t('calendar.modal.title.create')}
              </h2>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-[var(--color-surface-bg)] px-4 py-3 sm:px-5">
              <div className="space-y-2.5 sm:space-y-3">
                {readOnlyMeeting && (
                  <p className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface-page)] px-3 py-2 text-xs text-[var(--color-secondary)]">
                    {showInviteeRsvp
                      ? t('calendar.modal.readonly.invited')
                      : t('calendar.modal.readonly.viewer')}
                  </p>
                )}
                {showInviteeRsvp && (
                  <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface-elevated)]/40 p-3">
                    <div className="text-xs font-medium text-[var(--color-primary)] sm:text-sm">
                      {t('calendar.modal.yourResponse')}
                    </div>
                    {modalDetail?.mySeriesRsvp != null &&
                      myEffectiveRsvp != null &&
                      String(modalDetail.mySeriesRsvp) !== String(myEffectiveRsvp) && (
                        <p className="mt-2 text-[10px] leading-snug text-[var(--color-secondary)] sm:text-xs">
                          {t('calendar.modal.seriesAndOccurrence', {
                            series: formatCalendarRsvpLabel(modalDetail.mySeriesRsvp, t),
                            occurrence: formatCalendarRsvpLabel(myEffectiveRsvp, t)
                          })}
                        </p>
                      )}
                    <p className="mt-2 text-[10px] text-[var(--color-secondary)] sm:text-xs">
                      {t('calendar.modal.rsvpHelp')}
                    </p>
                    <CalendarRsvpChoice
                      className="mt-3"
                      value={normalizeCalendarRsvpValue(myEffectiveRsvp)}
                      onSelect={(v) => {
                        if (v === 'declined' && modalIsRecurringInstance) setRecurringDeclineOpen(true);
                        else void submitRsvp(v, { rsvpScope: 'series' });
                      }}
                      aria-label={t('calendar.modal.yourResponse')}
                    />
                  </div>
                )}
                <label className="block text-xs font-medium text-[var(--color-secondary)] sm:text-sm">
                  {t('calendar.field.title')}
                  <input
                    className="mt-0.5 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-page)] px-2.5 py-1.5 text-sm text-[var(--color-primary)] sm:px-3 sm:py-2 disabled:cursor-not-allowed disabled:opacity-60"
                    value={formTitle}
                    onChange={(e) => setFormTitle(e.target.value)}
                    disabled={readOnlyMeeting}
                  />
                </label>

                <label className="block text-xs font-medium text-[var(--color-secondary)] sm:text-sm">
                  {t('calendar.field.description')}
                  <textarea
                    className="mt-0.5 max-h-20 w-full resize-y rounded-md border border-[var(--color-border)] bg-[var(--color-surface-page)] px-2.5 py-1.5 text-sm text-[var(--color-primary)] sm:px-3 sm:py-2 disabled:cursor-not-allowed disabled:opacity-60"
                    rows={2}
                    value={formDescription}
                    onChange={(e) => setFormDescription(e.target.value)}
                    disabled={readOnlyMeeting}
                  />
                </label>

                <div className="space-y-2.5">
                  <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                    <label className="block text-xs font-medium text-[var(--color-secondary)] sm:text-sm">
                      {t('calendar.field.start')}
                      <input
                        type="datetime-local"
                        className="mt-0.5 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-page)] px-2.5 py-1.5 text-sm text-[var(--color-primary)] sm:px-3 sm:py-2 disabled:cursor-not-allowed disabled:opacity-60"
                        value={formStart}
                        onChange={(e) => {
                          const nextStart = e.target.value;
                          setFormStart(nextStart);
                          if (!isEndManuallySet) {
                            const defaultEnd = plusOneHourLocalInputValue(nextStart);
                            if (defaultEnd) setFormEnd(defaultEnd);
                          }
                        }}
                        disabled={readOnlyMeeting}
                      />
                    </label>
                    <label className="block text-xs font-medium text-[var(--color-secondary)] sm:text-sm">
                      {t('calendar.field.end')}
                      <input
                        type="datetime-local"
                        className="mt-0.5 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-page)] px-2.5 py-1.5 text-sm text-[var(--color-primary)] sm:px-3 sm:py-2 disabled:cursor-not-allowed disabled:opacity-60"
                        value={formEnd}
                        onChange={(e) => {
                          setFormEnd(e.target.value);
                          setIsEndManuallySet(true);
                        }}
                        disabled={readOnlyMeeting}
                      />
                    </label>
                  </div>

                  {Boolean(modalDetail?.occurrenceStartAt) && !readOnlyMeeting && (
                    <fieldset className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface-page)] px-2.5 py-2 sm:px-3">
                      <legend className="px-1 text-[10px] font-medium text-[var(--color-secondary)] sm:text-xs">
                        {t('calendar.editScope.datesLabel')}
                      </legend>
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--color-primary)] sm:text-sm">
                        <label className="inline-flex cursor-pointer items-center gap-1.5">
                          <input
                            type="radio"
                            name="cal-date-scope"
                            className="accent-[var(--color-accent-bg)]"
                            checked={formDateScope === 'this_occurrence'}
                            onChange={() => setFormDateScope('this_occurrence')}
                          />
                          {t('calendar.editScope.thisOccurrence')}
                        </label>
                        <label className="inline-flex cursor-pointer items-center gap-1.5">
                          <input
                            type="radio"
                            name="cal-date-scope"
                            className="accent-[var(--color-accent-bg)]"
                            checked={formDateScope === 'series'}
                            onChange={() => setFormDateScope('series')}
                          />
                          {t('calendar.editScope.entireSeries')}
                        </label>
                      </div>
                    </fieldset>
                  )}

                  <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface-page)] p-2.5 sm:p-3">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-3">
                      <label className="block min-w-0 flex-1 text-[10px] font-medium text-[var(--color-secondary)] sm:text-xs">
                        {t('calendar.field.unit')}
                        <select
                          className="mt-0.5 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-elevated)] px-2 py-1.5 text-sm text-[var(--color-primary)] disabled:cursor-not-allowed disabled:opacity-60"
                          value={formRecurrenceUnit}
                          onChange={(e) => {
                            const next = e.target.value as RecurrenceUnit;
                            setFormRecurrenceUnit(next);
                            if (next === 'none') {
                              setFormRecurrenceForever(true);
                              setFormRecurrenceEnd('');
                            } else if (!formRecurrenceEnd.trim()) {
                              setFormRecurrenceForever(true);
                            }
                          }}
                          disabled={readOnlyMeeting || lockSeriesRecurrence}
                        >
                          <option value="none">{t('calendar.recurrence.once')}</option>
                          <option value="daily">{t('calendar.recurrence.daily')}</option>
                          <option value="weekly">{t('calendar.recurrence.weekly')}</option>
                          <option value="monthly">{t('calendar.recurrence.monthly')}</option>
                          <option value="quarterly">{t('calendar.recurrence.quarterly')}</option>
                          <option value="yearly">{t('calendar.recurrence.yearly')}</option>
                        </select>
                      </label>
                      <label className="block w-full shrink-0 sm:w-[6.5rem] text-[10px] font-medium text-[var(--color-secondary)] sm:text-xs">
                        {t('calendar.field.frequency')}
                        <input
                          type="number"
                          min={1}
                          step={1}
                          disabled={readOnlyMeeting || lockSeriesRecurrence || formRecurrenceUnit === 'none'}
                          title={
                            formRecurrenceUnit === 'none'
                              ? t('calendar.recurrence.freqDisabledHint')
                              : undefined
                          }
                          className="mt-0.5 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-elevated)] px-2 py-1.5 text-sm text-[var(--color-primary)] disabled:cursor-not-allowed disabled:opacity-50"
                          value={formRecurrenceFrequency}
                          onChange={(e) =>
                            setFormRecurrenceFrequency(
                              Math.max(1, Number.parseInt(e.target.value || '1', 10) || 1)
                            )
                          }
                        />
                      </label>
                      {formRecurrenceUnit !== 'none' && (
                        <label className="block min-w-0 flex-1 text-[10px] font-medium text-[var(--color-secondary)] sm:text-xs">
                          {t('calendar.field.until')}
                          <select
                            className="mt-0.5 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-elevated)] px-2 py-1.5 text-sm text-[var(--color-primary)] disabled:cursor-not-allowed disabled:opacity-60"
                            value={formRecurrenceForever ? 'forever' : 'until'}
                            onChange={(e) => {
                              const forever = e.target.value === 'forever';
                              setFormRecurrenceForever(forever);
                              if (!forever && !formRecurrenceEnd.trim()) {
                                setFormRecurrenceEnd(defaultRecurrenceUntilEndOfYear());
                              }
                            }}
                            disabled={readOnlyMeeting || lockSeriesRecurrence}
                          >
                            <option value="forever">{t('calendar.recurrence.forever')}</option>
                            <option value="until">{t('calendar.recurrence.untilDate')}</option>
                          </select>
                        </label>
                      )}
                      {formRecurrenceUnit !== 'none' && !formRecurrenceForever && (
                        <label className="block min-w-0 flex-1 text-[10px] font-medium text-[var(--color-secondary)] sm:text-xs">
                          {t('calendar.recurrence.untilDate')}
                          <input
                            type="date"
                            className="mt-0.5 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-elevated)] px-2 py-1.5 text-sm text-[var(--color-primary)] disabled:cursor-not-allowed disabled:opacity-60"
                            value={formRecurrenceEnd}
                            onChange={(e) => setFormRecurrenceEnd(e.target.value)}
                            disabled={readOnlyMeeting || lockSeriesRecurrence}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                </div>

                <label className="block text-xs font-medium text-[var(--color-secondary)] sm:text-sm">
                  {t('calendar.field.sensitivity')}
                  <select
                    className="mt-0.5 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-page)] px-2.5 py-1.5 text-sm text-[var(--color-primary)] sm:px-3 sm:py-2 disabled:cursor-not-allowed disabled:opacity-60"
                    value={formSensitivity}
                    onChange={(e) => setFormSensitivity(e.target.value)}
                    disabled={readOnlyMeeting}
                  >
                    {SENSITIVITY_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {translateSensitivityLabel(s, t)}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface-page)] p-2.5 sm:p-3">
                  <div className="text-xs font-medium text-[var(--color-primary)] sm:text-sm">
                    {t('calendar.field.invitees')}
                  </div>
                  <p className="mt-0.5 text-[10px] leading-tight text-[var(--color-secondary)] sm:text-xs">
                    {t('calendar.invitees.hint')}
                  </p>
                  <div className="relative mt-2">
                    <input
                      type="text"
                      className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-2.5 py-1.5 text-sm text-[var(--color-primary)] sm:px-3 sm:py-2"
                      placeholder={
                        tenantUserDirectory.length === 0
                          ? t('calendar.invitees.loading')
                          : t('calendar.invitees.searchPlaceholder')
                      }
                      value={inviteeSearchQuery}
                      onChange={(e) => setInviteeSearchQuery(e.target.value)}
                      disabled={readOnlyMeeting || tenantUserDirectory.length === 0}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowDown') {
                          e.preventDefault();
                          setInviteeActiveIndex((i) =>
                            Math.min(i + 1, Math.max(0, inviteeSuggestions.length - 1))
                          );
                        } else if (e.key === 'ArrowUp') {
                          e.preventDefault();
                          setInviteeActiveIndex((i) => Math.max(0, i - 1));
                        } else if (e.key === 'Enter') {
                          e.preventDefault();
                          const pick = inviteeSuggestions[inviteeActiveIndex];
                          if (pick) {
                            if (!formInviteeIds.includes(pick.id)) {
                              setFormInviteeIds((prev) => [...prev, pick.id]);
                            }
                            setInviteeSearchQuery('');
                          } else if (inviteeSearchQuery.trim()) {
                            toast.error(t('calendar.invitees.noMatch'));
                          }
                        } else if (e.key === 'Escape') {
                          setInviteeSearchQuery('');
                        }
                      }}
                      autoComplete="off"
                      aria-autocomplete="list"
                      aria-expanded={inviteeSuggestions.length > 0}
                    />
                    {inviteeSuggestions.length > 0 && (
                      <ul
                        className="absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-auto rounded-md border border-[var(--color-border)] bg-[var(--color-surface-bg)] py-1 shadow-lg"
                        role="listbox"
                      >
                        {inviteeSuggestions.map((u, idx) => (
                          <li key={u.id} role="option" aria-selected={idx === inviteeActiveIndex}>
                            <button
                              type="button"
                              disabled={readOnlyMeeting}
                              className={`flex w-full flex-col items-start px-3 py-2 text-left text-sm ${
                                idx === inviteeActiveIndex
                                  ? 'bg-[var(--color-accent-bg)]/15 text-[var(--color-primary)]'
                                  : 'text-[var(--color-primary)] hover:bg-[var(--color-surface-elevated)]'
                              } disabled:cursor-not-allowed disabled:opacity-50`}
                              onMouseEnter={() => setInviteeActiveIndex(idx)}
                              onClick={() => {
                                if (readOnlyMeeting) return;
                                if (!formInviteeIds.includes(u.id)) {
                                  setFormInviteeIds((prev) => [...prev, u.id]);
                                }
                                setInviteeSearchQuery('');
                              }}
                            >
                              <span className="font-medium">{u.displayName}</span>
                              <span className="text-xs text-[var(--color-secondary)]">{u.email}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {formInviteeIds.length === 0 && (
                      <span className="text-xs text-[var(--color-secondary)]">{t('calendar.invitees.none')}</span>
                    )}
                    {formInviteeIds.map((id) => (
                      <span
                        key={id}
                        className="inline-flex items-center gap-1 rounded-full border border-[var(--color-border)] bg-[var(--color-surface-elevated)] px-2 py-0.5 text-xs text-[var(--color-primary)]"
                      >
                        <span>{inviteeLabelForId(id)}</span>
                        <button
                          type="button"
                          className="rounded p-0.5 text-[var(--color-secondary)] hover:bg-[var(--color-surface-page)] hover:text-[var(--color-primary)] disabled:pointer-events-none disabled:opacity-40"
                          onClick={() => setFormInviteeIds((prev) => prev.filter((x) => x !== id))}
                          aria-label={t('calendar.invitees.removeAria', { name: inviteeLabelForId(id) })}
                          disabled={readOnlyMeeting}
                        >
                          <XMarkIcon className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    ))}
                  </div>

                  {editingId && modalDetail && (
                    <div className="mt-2 text-[10px] text-[var(--color-secondary)] sm:text-xs">
                      {t('calendar.invitees.countSummary', {
                        count: (modalDetail.invitations ?? []).length
                      })}
                      {(modalDetail.invitations ?? []).length > 0 && (
                        <span>
                          {' '}
                          {t('calendar.invitees.stat.accepted')}{' '}
                          {
                            (modalDetail.invitations ?? []).filter((i) => i.rsvp === 'accepted')
                              .length
                          }
                          , {t('calendar.invitees.stat.tentative')}{' '}
                          {
                            (modalDetail.invitations ?? []).filter((i) => i.rsvp === 'tentative')
                              .length
                          }
                          , {t('calendar.invitees.stat.declined')}{' '}
                          {
                            (modalDetail.invitations ?? []).filter((i) => i.rsvp === 'declined')
                              .length
                          }
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  <label className="block text-xs font-medium text-[var(--color-secondary)] sm:text-sm">
                    {t('calendar.field.location')}
                    <input
                      type="text"
                      placeholder={t('calendar.location.placeholder')}
                      className="mt-0.5 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-page)] px-2.5 py-1.5 text-sm text-[var(--color-primary)] sm:px-3 sm:py-2 disabled:cursor-not-allowed disabled:opacity-60"
                      value={formPhysicalLocation}
                      onChange={(e) => setFormPhysicalLocation(e.target.value)}
                      autoComplete="street-address"
                      disabled={readOnlyMeeting}
                    />
                  </label>
                  <label className="block text-xs font-medium text-[var(--color-secondary)] sm:text-sm">
                    {t('calendar.field.onlineUrl')}
                    <input
                      type="url"
                      placeholder={t('calendar.url.placeholder')}
                      className="mt-0.5 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-page)] px-2.5 py-1.5 text-sm text-[var(--color-primary)] sm:px-3 sm:py-2 disabled:cursor-not-allowed disabled:opacity-60"
                      value={formMeetingUrl}
                      onChange={(e) => setFormMeetingUrl(e.target.value)}
                      inputMode="url"
                      disabled={readOnlyMeeting}
                    />
                  </label>
                </div>

                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-1">
                  <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface-page)] p-2.5 sm:p-3">
                    <div className="text-xs font-medium text-[var(--color-primary)] sm:text-sm">
                      {t('calendar.field.reminders')}
                    </div>
                    <p className="mt-0.5 text-[10px] leading-tight text-[var(--color-secondary)] sm:text-xs">
                      {t('calendar.reminders.scope')}
                    </p>
                    <div className="mt-2 grid max-h-[7.5rem] grid-cols-2 gap-x-2 gap-y-1 overflow-y-auto pr-0.5">
                      {CALENDAR_REMINDER_MINUTES.map((minutes) => (
                        <label
                          key={minutes}
                          className="flex cursor-pointer items-center gap-1.5 text-xs text-[var(--color-primary)] sm:text-sm"
                        >
                          <input
                            type="checkbox"
                            className="rounded border-[var(--color-border)]"
                            checked={formReminders.includes(minutes)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormReminders((prev) => [...prev, minutes].sort((a, b) => a - b));
                              } else {
                                setFormReminders((prev) => prev.filter((x) => x !== minutes));
                              }
                            }}
                            disabled={readOnlyMeeting}
                          />
                          <span className="leading-tight">{t(`calendar.reminder.m${minutes}`)}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                </div>

              </div>
            </div>

            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-[var(--color-border)] bg-[var(--color-surface-bg)] px-4 py-2.5 sm:px-5 sm:py-3">
              {editingId && (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] px-3 py-1.5 text-xs text-[var(--color-primary)] hover:bg-[var(--color-surface-page)] sm:px-4 sm:py-2 sm:text-sm"
                  onClick={() => void downloadIcs()}
                >
                  <ArrowDownTrayIcon className="h-4 w-4" aria-hidden />
                  {t('calendar.footer.ics')}
                </button>
              )}
              {!readOnlyMeeting && editingId && modalDetail && (
                <button
                  type="button"
                  className="rounded-md px-3 py-1.5 text-xs text-red-700 hover:bg-red-50 sm:px-4 sm:py-2 sm:text-sm"
                  onClick={() => requestCancel(modalDetail)}
                >
                  {t('calendar.action.cancelMeeting')}
                </button>
              )}
              <button
                type="button"
                className="rounded-md px-3 py-1.5 text-xs text-[var(--color-secondary)] hover:bg-[var(--color-surface-page)] sm:px-4 sm:py-2 sm:text-sm"
                onClick={() => setModalOpen(false)}
              >
                {t('calendar.footer.close')}
              </button>
              {!readOnlyMeeting && (
                <button
                  type="button"
                  className="rounded-md bg-[var(--color-accent-bg)] px-3 py-1.5 text-xs font-medium text-[var(--color-accent-text)] hover:opacity-90 sm:px-4 sm:py-2 sm:text-sm"
                  onClick={() => void saveEvent()}
                >
                  {t('calendar.footer.save')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {recurringDeclineOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-neutral-950/80 p-3 sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cal-decline-recurring-title"
        >
          <div className="w-full max-w-md rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)] p-4 shadow-2xl sm:p-5">
            <h3 id="cal-decline-recurring-title" className="text-base font-semibold text-[var(--color-primary)]">
              {t('calendar.decline.title')}
            </h3>
            <p className="mt-2 text-sm text-[var(--color-secondary)]">{t('calendar.decline.body')}</p>
            {modalDetail && (
              <p className="mt-2 text-xs text-[var(--color-secondary)]">
                {modalDetail.title} ·{' '}
                {format(parseISO(modalDetail.startAt), 'MMM d, yyyy p', { locale: dfLocale })}
              </p>
            )}
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
              <button
                type="button"
                className="rounded-md border border-[var(--color-border)] px-3 py-2 text-sm font-medium text-[var(--color-primary)] hover:bg-[var(--color-surface-page)]"
                onClick={() => setRecurringDeclineOpen(false)}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface-page)] px-3 py-2 text-sm font-medium text-[var(--color-primary)] hover:bg-[var(--color-surface-elevated)]"
                onClick={() => {
                  setRecurringDeclineOpen(false);
                  void submitRsvp('declined', { rsvpScope: 'this_occurrence' });
                }}
              >
                {t('calendar.decline.onlyOccurrence')}
              </button>
              <button
                type="button"
                className="rounded-md bg-rose-600 px-3 py-2 text-sm font-medium text-white hover:bg-rose-700"
                onClick={() => {
                  setRecurringDeclineOpen(false);
                  void submitRsvp('declined', { rsvpScope: 'series' });
                }}
              >
                {t('calendar.decline.entireSeries')}
              </button>
            </div>
          </div>
        </div>
      )}

      {recurringCancelOpen && cancelTarget && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-neutral-950/80 p-3 sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cal-cancel-recurring-title"
        >
          <div className="w-full max-w-md rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)] p-4 shadow-2xl sm:p-5">
            <h3 id="cal-cancel-recurring-title" className="text-base font-semibold text-[var(--color-primary)]">
              {t('calendar.cancel.title')}
            </h3>
            <p className="mt-2 text-sm text-[var(--color-secondary)]">{t('calendar.cancel.body')}</p>
            <p className="mt-2 text-xs text-[var(--color-secondary)]">
              {cancelTarget.title} ·{' '}
              {format(parseISO(cancelTarget.startAt), 'MMM d, yyyy p', { locale: dfLocale })}
            </p>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
              <button
                type="button"
                className="rounded-md border border-[var(--color-border)] px-3 py-2 text-sm font-medium text-[var(--color-primary)] hover:bg-[var(--color-surface-page)]"
                onClick={() => {
                  setRecurringCancelOpen(false);
                  setCancelTarget(null);
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface-page)] px-3 py-2 text-sm font-medium text-[var(--color-primary)] hover:bg-[var(--color-surface-elevated)]"
                onClick={() => void performCancel(cancelTarget, 'this_occurrence')}
              >
                {t('calendar.cancel.onlyOccurrence')}
              </button>
              <button
                type="button"
                className="rounded-md bg-rose-600 px-3 py-2 text-sm font-medium text-white hover:bg-rose-700"
                onClick={() => void performCancel(cancelTarget, 'series')}
              >
                {t('calendar.cancel.entireSeries')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
